import React from 'react'

import {ROUTES} from 'constants/routes.constant'

export {default as publicRoutes} from './authRoutes'
export {default as externalRoutes} from './externalRoutes'

export const protectedRoutes = [
    {
        key: 'usersManagement',
        path: ROUTES.USER_MANAGEMENT,
        component: React.lazy(() => import('views/UsersManagement')),
    },
    {
        key: 'userDetails',
        path: ROUTES.USER_DETAILS,
        component: React.lazy(() => import('views/UserDetails')),
    },
    {
        key: 'adminsManagement',
        path: ROUTES.ADMIN_MANAGEMENT,
        component: React.lazy(() => import('views/AdminsManagement')),
    },
    {
        key: 'adminsDetails',
        path: ROUTES.ADMIN_DETAILS,
        component: React.lazy(() => import('views/AdminDetails')),
    },
    {
        key: 'clubsManagement',
        path: ROUTES.CLUBS_MANAGEMENT,
        component: React.lazy(() => import('views/ClubsManagement')),
    },
    {
        key: 'clubsDetails',
        path: ROUTES.CLUB_DETAILS,
        component: React.lazy(() => import('views/ClubsDetails')),
    },
    {
        key: 'clubEdit',
        path: ROUTES.CLUB_EDIT,
        component: React.lazy(() => import('views/ClubEdit')),
    },
    {
        key: 'clubsPostsManagement',
        path: ROUTES.CLUBS_POSTS_MANAGEMENT,
        component: React.lazy(() => import('views/ClubsPostsManagement')),
    },
    {
        key: 'clubsPostsByClub',
        path: ROUTES.CLUB_POSTS_BY_CLUB,
        component: React.lazy(() => import('views/ClubPostsByClub')),
    },
    {
        key: 'clubsPostNew',
        path: ROUTES.CLUB_POST_NEW,
        component: React.lazy(() => import('views/ClubPostNew')),
    },
    {
        key: 'clubsPostDetail',
        path: ROUTES.CLUB_POST_DETAILS,
        component: React.lazy(() => import('views/ClubPostDetail')),
    },
    {
        key: 'clubsPostEdit',
        path: ROUTES.CLUB_POST_EDIT,
        component: React.lazy(() => import('views/ClubPostEdit')),
    },
    {
        key: 'eventsManagement',
        path: ROUTES.EVENTS_MANAGEMENT,
        component: React.lazy(() => import('views/EventsManagement')),
    },
    {
        key: 'eventNew',
        path: ROUTES.EVENT_NEW,
        component: React.lazy(() => import('views/EventNew')),
    },
    {
        key: 'eventDetails',
        path: ROUTES.EVENT_DETAILS,
        component: React.lazy(() => import('views/EventDetails')),
    },
    {
        key: 'eventEdit',
        path: ROUTES.EVENT_EDIT,
        component: React.lazy(() => import('views/EventEdit')),
    },
    {
        key: 'competitionEventsManagement',
        path: ROUTES.COMPETITION_EVENTS_MANAGEMENT,
        component: React.lazy(() => import('views/CompetitionEventsManagement')),
    },
    {
        key: 'competitionEventNew',
        path: ROUTES.COMPETITION_EVENT_NEW,
        component: React.lazy(() => import('views/CompetitionEventNew')),
    },
    {
        // Registered after COMPETITION_EVENT_NEW so the static /new segment wins,
        // mirroring the EVENT_NEW / EVENT_DETAILS pair above. Deliberately kept out
        // of ADMIN_ALLOWED_ROUTES_BY_PERMISSIONS: competition event administration is
        // SuperAdmin-only, so PermissionRoute redirects everyone else.
        key: 'competitionEventDetails',
        path: ROUTES.COMPETITION_EVENT_DETAILS,
        component: React.lazy(() => import('views/CompetitionEventDetails')),
    },
    {
        // Build the minimum SuperAdmin result inbox: without this, a submitted result is
        // invisible to everyone once its id is unknown. Deliberately kept out of
        // ADMIN_ALLOWED_ROUTES_BY_PERMISSIONS - same SuperAdmin-only treatment as
        // competitionEventDetails above.
        key: 'competitionEventResultInbox',
        path: ROUTES.COMPETITION_EVENT_RESULT_INBOX,
        component: React.lazy(() => import('views/CompetitionEventResultInbox')),
    },
    {
        key: 'competitionEventErgRaceImport',
        path: ROUTES.COMPETITION_EVENT_ERGRACE_IMPORT,
        component: React.lazy(() => import('views/CompetitionEventErgRaceImport')),
    },
    {
        key: 'competitionEventRelayResults',
        path: ROUTES.COMPETITION_EVENT_RELAY_RESULTS,
        component: React.lazy(() => import('views/CompetitionEventRelayResults')),
    },
    {
        key: 'competitionEventResultInboxDetails',
        path: ROUTES.COMPETITION_EVENT_RESULT_INBOX_DETAILS,
        component: React.lazy(() => import('views/CompetitionEventResultInboxDetails')),
    },
    {
        // Registration for NSW Round 1 is open on production and taking real money with no
        // admin-visible roster before this. Same SuperAdmin-only treatment as above.
        key: 'competitionEventRegistrations',
        path: ROUTES.COMPETITION_EVENT_REGISTRATIONS,
        component: React.lazy(() => import('views/CompetitionEventRegistrations')),
    },
    {
        key: 'competitionEventRegistrationDetails',
        path: ROUTES.COMPETITION_EVENT_REGISTRATION_DETAILS,
        component: React.lazy(() => import('views/CompetitionEventRegistrationDetails')),
    },
    {
        key: 'competitionEventAffiliationQueue',
        path: ROUTES.COMPETITION_EVENT_AFFILIATION_QUEUE,
        component: React.lazy(() => import('views/CompetitionEventAffiliationQueue')),
    },
    {
        key: 'competitionEventTeamLinks',
        path: ROUTES.COMPETITION_EVENT_TEAM_LINKS,
        component: React.lazy(() => import('views/CompetitionEventTeamLinks')),
    },
    {
        // Aligner N2 (2026-08-08): the attempts READ seam did not exist anywhere on the
        // backend - only verify/unpublish/correct PATCHes - so BLE/app-published attempts
        // were invisible outside CloudShell SQL. Same SuperAdmin-only treatment as
        // competitionEventResultInbox/competitionEventRegistrations above.
        key: 'competitionEventAttempts',
        path: ROUTES.COMPETITION_EVENT_ATTEMPTS,
        component: React.lazy(() => import('views/CompetitionEventAttempts')),
    },
    {
        key: 'competitionEventWeighIns',
        path: ROUTES.COMPETITION_EVENT_WEIGH_INS,
        component: React.lazy(() => import('views/CompetitionEventWeighIns')),
    },
    {
        key: 'workoutsManagement',
        path: ROUTES.WORKOUTS_MANAGEMENT,
        component: React.lazy(() => import('views/WorkoutsManagement')),
    },
    {
        key: 'workoutLogsManagement',
        path: ROUTES.WORKOUT_LOGS_MANAGEMENT,
        component: React.lazy(() => import('views/WorkoutLogsManagement')),
    },
    {
        key: 'workoutCreate',
        path: ROUTES.WORKOUT_CREATE,
        component: React.lazy(() => import('views/WorkoutCreate')),
    },
    {
        key: 'workoutDetails',
        path: ROUTES.WORKOUT_DETAILS,
        component: React.lazy(() => import('views/WorkoutDetails')),
    },
    {
        key: 'workoutEdit',
        path: ROUTES.WORKOUT_EDIT,
        component: React.lazy(() => import('views/WorkoutEdit')),
    },
    {
        key: 'programsManagement',
        path: ROUTES.PROGRAMS_MANAGEMENT,
        component: React.lazy(() => import('views/ProgramsManagement')),
    },
    {
        key: 'programCreate',
        path: ROUTES.PROGRAMS_CREATE,
        component: React.lazy(() => import('views/ProgramCreate')),
    },
    // {
    //     key: 'programDetails',
    //     path: ROUTES.PROGRAMS_DETAILS,
    //     component: React.lazy(() => import('views/ProgramDetails')),
    // },
    {
        key: 'programEdit',
        path: ROUTES.PROGRAMS_EDIT,
        component: React.lazy(() => import('views/ProgramEdit')),
    },
    {
        key: 'categoriesManagement',
        path: ROUTES.CATEGORIES_MANAGEMENT,
        component: React.lazy(() => import('views/CategoriesManagement')),
    },
    {
        key: 'featuredManagement',
        path: ROUTES.FEATURED_MANAGEMENT,
        component: React.lazy(() => import('views/FeaturedManagement')),
    },
    {
        key: 'commentsManagement',
        path: ROUTES.COMMENTS_MANAGEMENT,
        component: React.lazy(() => import('views/CommentsManagement')),
    },
    {
        key: 'commentsByType',
        path: ROUTES.COMMENTS_BY_TYPE,
        component: React.lazy(() => import('views/CommentsSpecificList')),
    },
    {
        key: 'reportsManagement',
        path: ROUTES.REPORTS_MANAGEMENT,
        component: React.lazy(() => import('views/ReportsManagement')),
    },
    {
        key: 'blockedUserManagement',
        path: ROUTES.BLOCKED_USER_MANAGEMENT,
        component: React.lazy(() => import('views/BlockedUserDetails')),
    },
    {
        key: 'dashboard',
        path: ROUTES.DASHBOARD,
        component: React.lazy(() => import('views/Dashboard')),
    },
    {
        key: 'guideWattsZones',
        path: ROUTES.GUIDE_WATTS_ZONES,
        component: React.lazy(() => import('views/GuideWattsZones')),
    },
]
